/**
 * Busca e resolução de posições (endereços) — pesquisa case-sensitive na API (`address*cc*`).
 */
import api, { getSialApiHost } from '../api.js';
import db from '../../versa/db.js';

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/** Rótulo do endereço (ex.: A-1-1). */
export function fmtLocAddr(a)
{
	return Array.isArray(a) ? a.join('-') : String(a ?? '');
}

function escapeFilterToken(s)
{
	return String(s ?? '').replace(/[*,]/g, '');
}

/**
 * Resolve empresa (UUID) a partir de id ou nick digitado no formulário.
 * @param {string} raw
 * @returns {Promise<string|null>}
 */
export async function resolveCompanyId(raw)
{
	const v = String(raw ?? '').trim();
	if (!v)
		return null;
	if (UUID_REGEX.test(v))
		return v;
	const companies = await db.list('companies').catch(() => []);
	const c = companies.find(x =>
		(x.nick || '') === v || String(x.nick || '') === v
		|| (x.name || '') === v || String(x.name || '') === v);
	const id = c?.id;
	return id && UUID_REGEX.test(String(id)) ? String(id) : null;
}

/**
 * Lista posições do armazém na API; com `search`, filtro contém case-sensitive no endereço.
 * @param {string} companyId
 * @param {number} warehouseId
 * @param {{ search?: string, limit?: number }} [opts]
 * @returns {Promise<object[]>}
 */
export async function fetchWarehouseLocations(companyId, warehouseId, opts = {})
{
	const co = String(companyId ?? '').trim();
	const wh = Number(warehouseId);
	if (!co || !Number.isInteger(wh) || wh < 1)
		return [];
	const parts = [`company_id*eq*${co}`, `warehouse_id*eq*${wh}`];
	const term = String(opts.search ?? '').trim();
	if (term)
		parts.push(`address*cc*${escapeFilterToken(term)}`);
	const params = new URLSearchParams();
	params.append('filter', parts.join(','));
	params.append('sort', '4');
	params.append('limit', String(term ? (opts.limit ?? 80) : (opts.limit ?? 0)));
	const q = `?${params.toString()}`;
	const rows = opts.silent
		? await fetchLocationsSilent(q)
		: await api.call(api.list_locations, q);
	return Array.isArray(rows) ? rows : [];
}

/** Busca posições sem msgbox «Carregando…» (digitação no datalist). */
async function fetchLocationsSilent(query)
{
	try
	{
		const res = await fetch(`${getSialApiHost()}/locations${query}`, {
			method: 'GET',
			credentials: 'include',
		});
		if (!res.ok)
			return [];
		const data = await res.json();
		return Array.isArray(data) ? data : [];
	}
	catch (_)
	{
		return [];
	}
}

/**
 * Resolve id da posição a partir de id numérico ou texto do endereço (case-sensitive).
 * @param {string} companyId
 * @param {number} warehouseId
 * @param {string|number} raw
 * @returns {Promise<number>}
 */
export async function resolveLocationIdForMove(companyId, warehouseId, raw)
{
	const rawStr = String(raw ?? '').trim();
	if (!rawStr)
		return NaN;
	const n = Number(rawStr);
	if (Number.isInteger(n) && n >= 1)
		return n;
	const rows = await fetchWarehouseLocations(companyId, warehouseId, { search: rawStr, limit: 80 });
	if (!rows.length)
		return NaN;
	if (rows.length === 1)
		return Number(rows[0].id);
	const want = rawStr;
	for (const r of rows)
	{
		if (fmtLocAddr(r.address) === want)
			return Number(r.id);
	}
	return NaN;
}
